import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faClock,
  faCheckCircle,
  faUser,
  faCalendarAlt,
  faCircle,
  faEye,
  faPlay,
  faWrench,
  faChevronRight,
  faCheck,
  faSpinner,
  faTable,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import type { WorkflowTask } from "@/services/api/agentic/types";
import {
  getAgenticWorkflowTasks,
  getPodInstanceWorkflowStages,
  getPodInstance,
} from "@/services/api/agentic";
import { LoadingIndicator } from "@/components/indicators/LoadingIndicator";
import {
  faFile,
  faTimelineArrow,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { CallToAction } from "./CallToAction";
import { Tabs } from "@/components/ui/tabs/Tabs";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from "@/components/ui/table/Table";
import Drawer from "@/components/ui/drawer";
import {
  podCurrentStatusToDisplayString,
  podCurrentStatusToMachineString,
} from "@/utils/podStatus";

interface StageType {
  _id: string;
  name: string;
  description?: string;
  progress_percentage?: number;
  call_to_actions?: any[];
  notes?: string;
  current_state?: string;
  recent_completed_jobs?: Array<{
    _id: string;
    description: string;
    completed_at: string;
    type?: string;
    duration_seconds?: number;
  }>;
  live_system_jobs?: Array<{
    _id: string;
    description: string;
    type?: string;
    duration_seconds?: number;
    started_at: string;
  }>;
  status?:
    | string
    | {
        current_status: string;
        current_stage: string;
        blocked_reason: string | null;
        progress_percentage: number;
        call_to_action: string;
      };
  // Add other fields if needed
}

interface TaskChecklistProps {
  instanceId: string;
  height?: number; // in pixels
}

export function TaskChecklist({ instanceId, height }: TaskChecklistProps) {
  const [tasks, setTasks] = useState<WorkflowTask[]>([]);
  const [stages, setStages] = useState<StageType[]>([]);
  const [podInstance, setPodInstance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedStageForDrawer, setSelectedStageForDrawer] =
    useState<StageType | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const initialScrollDone = useRef(false);
  const isProgrammaticScroll = useRef(false);
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second for live job timers
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Helper function to calculate elapsed seconds
  const getElapsedSeconds = (startedAt: string) => {
    const startTime = new Date(startedAt).getTime();
    return Math.floor((currentTime - startTime) / 1000);
  };

  // Fetch tasks, stages, and pod instance data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [fetchedTasks, fetchedStages, fetchedPodInstance] =
        await Promise.all([
          getAgenticWorkflowTasks(instanceId),
          getPodInstanceWorkflowStages(instanceId),
          getPodInstance(instanceId),
        ]);
      setTasks(fetchedTasks);
      setStages(
        Array.isArray(fetchedStages?.workflow_tasks)
          ? fetchedStages.workflow_tasks
          : []
      );
      setPodInstance(fetchedPodInstance);
    } catch (err) {
      setError("Failed to fetch tasks, stages, or pod instance");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => {
    fetchData();
    // Poll every 60 seconds instead of 30 to reduce server load
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element || height != null) return;

    const updateHeight = () => {
      const nextHeight = element.clientHeight;
      if (nextHeight > 0) {
        setContainerHeight((prev) => (prev === nextHeight ? prev : nextHeight));
      }
    };

    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [height]);

  // Timeline items are now the workflow stages
  const timelineItems = stages;
  // Tab header: ~92px (16px top + 60px min-height + 16px bottom)
  // Content wrapper padding (staticTabs): 16px bottom
  // Total chrome height to subtract
  const tabChromeHeight = 92 + 16; // header + bottom padding
  const resolvedHeight = height ?? containerHeight;
  const totalHeight = resolvedHeight > 0 ? resolvedHeight : 360;
  // Available height for timeline content after subtracting tab chrome
  const timelineHeight =
    resolvedHeight > 0 ? totalHeight - tabChromeHeight : 360;
  const itemHeight = Math.floor(timelineHeight / 3);
  const headerHeight = itemHeight;
  const topSpacerHeight = Math.max(0, (timelineHeight - 3 * itemHeight) / 2);
  const bottomSpacerHeight = itemHeight;

  // Set initial selected index based on current stage
  useEffect(() => {
    if (
      podInstance &&
      stages.length > 0 &&
      !initialScrollDone.current &&
      scrollContainerRef.current
    ) {
      // Check if user has not scrolled
      if (scrollContainerRef.current.scrollTop === 0) {
        const rawStage = podInstance.state?.current_stage;
        const stageMatch =
          podCurrentStatusToDisplayString(rawStage) ||
          podCurrentStatusToMachineString(rawStage);
        if (stageMatch) {
          const stageIndex = stages.findIndex(
            (stage) => stage.name === stageMatch
          );
          if (stageIndex !== -1) {
            setSelectedIndex(stageIndex);
            // Scroll to the current stage using smart scrolling
            setTimeout(() => {
              // A final check to ensure user hasn't scrolled in the meantime
              if (
                scrollContainerRef.current &&
                scrollContainerRef.current.scrollTop === 0
              ) {
                scrollToStage(stageIndex);
                initialScrollDone.current = true;
              }
            }, 100);
          }
        }
      } else {
        // If user has already scrolled, mark initial scroll as done
        initialScrollDone.current = true;
      }
    }
  }, [podInstance, stages]);

  // Smart scrolling function: jump to nearby position, then smooth scroll to target
  const scrollToStage = (targetIndex: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    isProgrammaticScroll.current = true;

    const containerHeight = container.clientHeight;
    const currentScrollTop = container.scrollTop;

    // Calculate target scroll position
    const targetScrollTop =
      headerHeight +
      topSpacerHeight +
      targetIndex * itemHeight +
      itemHeight / 2 -
      containerHeight / 2;

    // Calculate current center position
    const currentCenterPosition = currentScrollTop + containerHeight / 2;
    const currentCenterIndex = Math.floor(
      (currentCenterPosition - headerHeight - topSpacerHeight) / itemHeight
    );

    // If we're already close to the target (within 1 item), just smooth scroll
    if (Math.abs(currentCenterIndex - targetIndex) <= 1) {
      container.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
      return;
    }

    // Otherwise, jump to a nearby position first, then smooth scroll
    const jumpIndex =
      targetIndex > currentCenterIndex
        ? Math.max(0, targetIndex - 1) // Jump to 1 item before target
        : Math.min(timelineItems.length - 1, targetIndex + 1); // Jump to 1 item after target

    const jumpScrollTop =
      headerHeight +
      topSpacerHeight +
      jumpIndex * itemHeight +
      itemHeight / 2 -
      containerHeight / 2;

    // First jump (instant)
    container.scrollTo({
      top: jumpScrollTop,
      behavior: "auto",
    });

    // Then smooth scroll to the final target
    setTimeout(() => {
      container.scrollTo({
        top: targetScrollTop,
        behavior: "smooth",
      });
      // Allow for scroll to finish
      setTimeout(() => {
        isProgrammaticScroll.current = false;
      }, 500);
    }, 50);
  };

  // Scroll logic: update selectedIndex based on scroll position
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // On any user scroll, we prevent the initial auto-scroll.
    if (!initialScrollDone.current) {
      initialScrollDone.current = true;
    }

    const container = e.currentTarget;
    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const centerPosition = scrollTop + containerHeight / 2;
    const adjustedCenterPosition =
      centerPosition - headerHeight - topSpacerHeight;
    const centerIndex = Math.floor(adjustedCenterPosition / itemHeight);
    const clampedIndex = Math.max(
      0,
      Math.min(timelineItems.length - 1, centerIndex)
    );
    if (clampedIndex !== selectedIndex) {
      setSelectedIndex(clampedIndex);
    }
  };

  if (loading && tasks.length === 0 && stages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center flex flex-col items-center gap-4">
          <LoadingIndicator dotCount={4} showText={false} />
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
            Loading Timeline...
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Please wait while we fetch the timeline for this instance.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center flex flex-col items-center gap-4">
          <h3 className="text-lg font-semibold text-red-500 dark:text-red-400 mb-2">
            Error: {error}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Failed to load timeline. Please try again.
          </p>
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-300 text-white rounded-md transition-colors duration-200"
          >
            {loading ? "Refreshing..." : "Retry"}
          </button>
        </div>
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center flex flex-col items-center gap-4">
          <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">
            No Timeline
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            No workflow stages found for this instance.
          </p>
        </div>
      </div>
    );
  }

  const MIN_PROGRESS = 1;

  // Helper to get status info
  const getStageStatus = (stage: StageType): string => {
    if (typeof stage.status === "string") {
      return stage.status;
    }
    if (typeof stage.status === "object" && stage.status?.current_status) {
      return (
        podCurrentStatusToMachineString(stage.status.current_status) ||
        podCurrentStatusToDisplayString(stage.status.current_status) ||
        "Not Started"
      );
    }
    return "Not Started";
  };

  // Helper to render status badge
  const renderStatusBadge = (
    status: string,
    hasIssues: boolean,
    showArrow: boolean = false,
    ctaPriority?: string
  ) => {
    let bgColor = "bg-slate-200/80 dark:bg-slate-900/20";
    let textColor = "text-slate-600 dark:text-slate-400";
    let icon = faCircle;

    const statusLower = status.toLowerCase();
    const isInProgress = statusLower.includes("progress");
    const isReview = statusLower.includes("review");

    // Check status based on lowercase comparison for more flexibility
    if (statusLower.includes("completed") || statusLower === "complete") {
      bgColor = "bg-green-500/20";
      textColor = "text-green-600 dark:text-green-400";
      icon = faCheckCircle;
    } else if (isInProgress) {
      bgColor = "bg-slate-200/80 dark:bg-slate-900/20";
      textColor = "text-slate-600 dark:text-slate-400";
      icon = faSpinner;
    } else if (isReview) {
      // Use CTA priority color if available, otherwise use default amber
      if (ctaPriority) {
        const priorityLower = ctaPriority.toLowerCase();
        if (priorityLower === "high") {
          bgColor = "bg-red-500/20";
          textColor = "text-red-600 dark:text-red-400";
          icon = faExclamationTriangle;
        } else if (priorityLower === "medium") {
          bgColor = "bg-yellow-500/20";
          textColor = "text-yellow-600 dark:text-yellow-400";
          icon = faEye;
        } else if (priorityLower === "low") {
          bgColor = "bg-green-500/20";
          textColor = "text-green-600 dark:text-green-400";
          icon = faEye;
        } else {
          bgColor = "bg-amber-500/20";
          textColor = "text-amber-600 dark:text-amber-400";
          icon = faEye;
        }
      } else {
        bgColor = "bg-amber-500/20";
        textColor = "text-amber-600 dark:text-amber-400";
        icon = faEye;
      }
    } else if (
      statusLower.includes("scheduled") ||
      statusLower.includes("schedule")
    ) {
      bgColor = "bg-gray-500/20";
      textColor = "text-gray-600 dark:text-gray-400";
      icon = faClock;
    } else if (statusLower.includes("blocked") || hasIssues) {
      bgColor = "bg-red-500/20";
      textColor = "text-red-600 dark:text-red-400";
      icon = hasIssues ? faExclamationTriangle : faCircle;
    }

    return (
      <span
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-medium ${bgColor} ${textColor}`}
      >
        {isInProgress ? (
          <LoadingIndicator
            dotSize={2}
            dotCount={3}
            showText={false}
            dotSpacing={1}
          />
        ) : (
          <FontAwesomeIcon icon={icon} />
        )}
        {status}
        {showArrow && <span>&rarr;</span>}
      </span>
    );
  };

  // Helper function to get progress bar color based on status
  const getProgressBarColor = (
    status: string,
    hasIssues: boolean,
    ctaPriority?: string
  ): string => {
    const statusLower = status.toLowerCase();

    if (statusLower.includes("completed") || statusLower === "complete") {
      return "bg-green-500";
    } else if (statusLower.includes("review")) {
      // Use CTA priority color if available, otherwise use default amber
      if (ctaPriority) {
        const priorityLower = ctaPriority.toLowerCase();
        if (priorityLower === "high") {
          return "bg-red-400";
        } else if (priorityLower === "medium") {
          return "bg-yellow-400";
        } else if (priorityLower === "low") {
          return "bg-green-500";
        }
      }
      return "bg-amber-400";
    } else if (statusLower.includes("blocked") || hasIssues) {
      return "bg-red-400";
    } else if (
      statusLower.includes("scheduled") ||
      statusLower.includes("schedule")
    ) {
      return "bg-gray-500";
    } else {
      // Default for in-progress or unknown
      return "bg-indigo-500";
    }
  };

  // Render the overview table
  const renderOverviewTable = () => {
    return (
      <div className="h-full overflow-auto p-6">
        <Table striped>
          <TableHead>
            <TableRow>
              <TableHeader className="w-8">#</TableHeader>
              <TableHeader>Stage</TableHeader>
              <TableHeader className="text-center w-40">Progress</TableHeader>
              <TableHeader className="w-48">Status</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {stages.map((stage, index) => {
              const status = getStageStatus(stage);
              const hasIssues =
                Array.isArray(stage.call_to_actions) &&
                stage.call_to_actions.length > 0;
              const progressValue =
                typeof stage.progress_percentage === "number"
                  ? stage.progress_percentage
                  : 0;
              const progress = Math.round(progressValue * 100);

              // Calculate highest priority from call_to_actions
              let highestPriority = "";
              if (hasIssues && stage.call_to_actions) {
                highestPriority = stage.call_to_actions.reduce(
                  (highest: string, cta: any) => {
                    const priority = (cta.priority || "").toLowerCase();
                    if (priority === "high") return "high";
                    if (priority === "medium" && highest !== "high")
                      return "medium";
                    if (
                      priority === "low" &&
                      highest !== "high" &&
                      highest !== "medium"
                    )
                      return "low";
                    return highest;
                  },
                  ""
                );
              }

              const progressColor = getProgressBarColor(
                status,
                hasIssues,
                highestPriority
              );
              const isReviewStatus = status.toLowerCase().includes("review");

              return (
                <TableRow key={stage._id}>
                  <TableCell className="text-slate-500 dark:text-slate-400 font-mono text-xs">
                    {index + 1}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900 dark:text-slate-100">
                    <div className="flex items-center gap-2">{stage.name}</div>
                  </TableCell>
                  <TableCell className="text-center w-40">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-24 h-2 bg-slate-200 dark:bg-slate-900/20 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${progressColor} transition-all duration-300 rounded-full`}
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono text-slate-600 dark:text-slate-400 w-10 text-right">
                        {progress}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="w-48">
                    <div
                      onClick={
                        hasIssues
                          ? () => {
                              setSelectedStageForDrawer(stage);
                              setIsDrawerOpen(true);
                            }
                          : undefined
                      }
                      className={
                        hasIssues
                          ? "cursor-pointer hover:opacity-80 transition-opacity"
                          : ""
                      }
                    >
                      {renderStatusBadge(
                        status,
                        hasIssues,
                        hasIssues && isReviewStatus,
                        highestPriority
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  // Render the timeline view (extracted from original render)
  const renderTimelineView = () => {
    // Use the timeline height and calculations from the outer scope
    // which already accounts for the tab header height
    return (
      <div className="flex relative w-full overflow-hidden h-full">
        {/* Refresh indicator */}
        {/* {loading && (tasks.length > 0 || stages.length > 0) && (
        <div className="absolute top-2 right-2 z-10">
          <div className="flex items-center gap-2 px-3 py-1 bg-slate-800/80 backdrop-blur-sm rounded-full text-xs text-slate-300">
            <LoadingIndicator dotCount={3} dotSize={5} />
            <span>Refreshing...</span>
          </div>
        </div>
      )} */}
        {/* Timeline Sidebar */}
        <div className="w-96 ml-4 flex flex-col min-h-0 bg-transparent z-10 h-full">
          {/* Scrollable Timeline Container */}
          <div
            ref={scrollContainerRef}
            className="flex-1 min-h-0 relative overflow-y-auto scroll-smooth group/timeline"
            onScroll={handleScroll}
            style={{ scrollSnapType: "y mandatory" }}
          >
            <div className="relative">
              {/* Main timeline line hidden for now */}
              <div className="flex flex-col items-center">
                {/* Header with title */}
                <div
                  className="flex-shrink-0 px-4 flex flex-col justify-center items-center"
                  style={{
                    height: `${itemHeight}px`,
                    scrollSnapAlign: "center",
                  }}
                >
                  <div className="text-lg flex items-center gap-2 text-center font-semibold text-slate-900 dark:text-slate-100">
                    <FontAwesomeIcon
                      icon={faTimelineArrow}
                      rotation={90}
                      className="mr-2 text-xl"
                    />
                    <div className="text-xl">Timeline</div>
                  </div>
                </div>
                {/* Spacer to start first item in center (pixel-based) */}
                <div
                  className="flex-shrink-0"
                  style={{ height: `${topSpacerHeight}px` }}
                ></div>
                {timelineItems.map((stage, index) => {
                  const isSelected = selectedIndex === index;
                  const isInCenterPosition = isSelected;
                  // Muted/faded effect for inactive items
                  const itemText = isInCenterPosition
                    ? "text-slate-900 dark:text-white"
                    : "text-slate-600 dark:text-slate-400";
                  const itemBg = isInCenterPosition ? "" : "";
                  const indicatorBg = isInCenterPosition ? "bg-indigo-400" : "";
                  const indicatorText = isInCenterPosition
                    ? "text-slate-900 dark:text-white"
                    : "text-slate-600 dark:text-slate-400";
                  // Use progress_percentage for the progress ring, but always show at least MIN_PROGRESS
                  // Progress comes as a float (0.0 to 1.0), convert to percentage
                  const progressValue =
                    typeof stage.progress_percentage === "number"
                      ? stage.progress_percentage
                      : 0;
                  const progress = Math.max(MIN_PROGRESS, progressValue * 100);
                  // For the label, show the actual progress percentage
                  const actualProgress = Math.round(progressValue * 100);
                  return (
                    <div
                      key={stage._id}
                      className={`relative px-4 group cursor-pointer w-full flex items-center justify-center hover:bg-white/50 dark:hover:bg-slate-900/50 rounded-2xl transition-all duration-200 ${itemText}`}
                      style={{
                        scrollSnapAlign: "center",
                        height: `${itemHeight}px`,
                      }}
                      onClick={() => {
                        initialScrollDone.current = true;
                        const containerHeight =
                          scrollContainerRef.current?.clientHeight ||
                          timelineHeight - headerHeight;
                        const targetScrollTop =
                          headerHeight +
                          topSpacerHeight +
                          index * itemHeight +
                          itemHeight / 2 -
                          containerHeight / 2;
                        scrollContainerRef.current?.scrollTo({
                          top: targetScrollTop,
                          behavior: "smooth",
                        });
                      }}
                    >
                      <div className="flex  items-center flex-col gap-6 px-4 py-6 w-full">
                        <div
                          className={`w-14 z-20 flex items-center justify-center`}
                        >
                          {/* Step indicator */}
                          <div
                            className={`relative flex-shrink-0 z-10 transition-all duration-300 origin-center ${
                              isInCenterPosition ? "size-14" : "size-8"
                            }`}
                          >
                            <div
                              className={`w-full h-full font-mono rounded-full flex items-center justify-center transition-all duration-300 font-normal text-xs ${indicatorBg} ${indicatorText}`}
                            >
                              {isInCenterPosition ? (
                                <span className="text-base flex gap-1 items-center">
                                  <div>{actualProgress}</div>
                                  <span className="text-xs uppercase relative top-[1px] leading-none">
                                    %
                                  </span>
                                </span>
                              ) : (
                                <span className="text-base">{index + 1}</span>
                              )}
                            </div>
                            {/* Progress ring for this stage (currently just min) */}
                            <div
                              className={`absolute -inset-4 ${
                                isInCenterPosition ? "opacity-100" : "opacity-0"
                              }`}
                              style={
                                {
                                  "--progress-circle-bg":
                                    "rgba(148, 163, 184, 0.2)",
                                  "--progress-circle-fg": "#818cf8",
                                } as React.CSSProperties
                              }
                            >
                              <svg
                                className="w-full h-full"
                                viewBox="0 0 36 36"
                              >
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="15"
                                  fill="none"
                                  stroke="var(--progress-circle-bg)"
                                  strokeWidth="2"
                                />
                                <circle
                                  cx="18"
                                  cy="18"
                                  r="15"
                                  fill="none"
                                  stroke="var(--progress-circle-fg)"
                                  strokeWidth="2"
                                  strokeDasharray={`${2 * Math.PI * 15}`}
                                  strokeDashoffset={`${
                                    2 * Math.PI * 15 * (1 - progress / 100)
                                  }`}
                                  transform="rotate(-90 18 18)"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                        {/* Step content */}
                        <div
                          className={`flex-1 min-w-0 text-center  pt-2 ${itemBg}`}
                        >
                          <div className="flex items-center justify-center mb-1">
                            <h4 className={`font-medium text-lg ${itemText}`}>
                              {stage.name}
                            </h4>
                          </div>
                          <p
                            className={`text-sm font-mono leading-relaxed max-w-64 transition-opacity duration-200 ${
                              isInCenterPosition
                                ? "text-slate-400"
                                : "text-slate-500"
                            }`}
                          >
                            {isInCenterPosition ? stage.description : null}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Bottom spacer to allow last item to center (pixel-based) */}
              <div
                className="flex-shrink-0"
                style={{ height: `${bottomSpacerHeight}px` }}
              ></div>
            </div>
          </div>
        </div>
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-0 overflow-y-auto z-10 h-full">
          <div className="flex items-center justify-center p-6 h-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={selectedIndex}
                layout
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.25 }}
                className="w-full max-w-2xl"
              >
                {/* Show details for the selected stage (or task) */}
                {timelineItems[selectedIndex] &&
                Array.isArray(timelineItems[selectedIndex].call_to_actions) &&
                timelineItems[selectedIndex].call_to_actions.length > 0 ? (
                  <>
                    <div className="text-2xl mb-4 font-semibold text-slate-900 dark:text-slate-100">
                      We've got some bits we need to sort...
                    </div>
                    <div className="space-y-3 max-w-[420px]">
                      <div className="group flex items-start gap-4 p-4">
                        {/* Task Content */}
                        <div className="flex-1 min-w-0 space-y-4">
                          <div className="grid grid-cols-1 gap-4 text-sm">
                            {timelineItems[selectedIndex].call_to_actions.map(
                              (action: any, i: number) => (
                                <div key={action.id || i} className="p-4 flex flex-col rounded-xl space-y-4 transition-all duration-200 bg-white dark:bg-slate-700 hover:shadow-sm">
                                  <CallToAction
                                    action={action}
                                  />
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (timelineItems[selectedIndex]?.status === "In Progress" ||
                    (typeof timelineItems[selectedIndex]?.status === "object" &&
                      timelineItems[selectedIndex]?.status?.current_status ===
                        "In Progress")) &&
                  (timelineItems[selectedIndex]?.live_system_jobs ||
                    timelineItems[selectedIndex]?.recent_completed_jobs) ? (
                  <div>
                    <div className="text-lg pb-4 px-4">
                      {podCurrentStatusToDisplayString(
                        timelineItems[selectedIndex]?.current_state
                      ) ||
                        podCurrentStatusToMachineString(
                          timelineItems[selectedIndex]?.current_state
                        )}
                    </div>
                    <div className="w-full max-w-xl font-mono text-sm flex justify-center items-center flex-col gap-2 bg-slate-100/90 dark:bg-slate-900/20 rounded-2xl p-6 border border-slate-200/60 dark:border-transparent">
                      <div className="flex flex-col gap-2">
                        {/* Show completed jobs first */}
                        {timelineItems[
                          selectedIndex
                        ].recent_completed_jobs?.map((job, index) => (
                          <div
                            key={`completed-${job._id}`}
                            className="flex items-start text-slate-400"
                          >
                            <div className="w-8 flex-none relative left-1">
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="text-slate-400"
                              />
                            </div>
                            <span className="text-slate-900 dark:text-white">
                              {job.description}
                            </span>
                            {job.duration_seconds && (
                              <span className="text-xs py-0.5 px-4 text-slate-400">
                                {job.duration_seconds}s
                              </span>
                            )}
                          </div>
                        ))}
                        {/* Show live system jobs after completed jobs */}
                        {timelineItems[selectedIndex].live_system_jobs?.map(
                          (job, index) => (
                            <div
                              key={`live-${job._id}`}
                              className="flex items-start text-slate-400"
                            >
                              <div className="w-8 flex-none relative left-1 top-0.5">
                                <LoadingIndicator
                                  dotSize={3}
                                  dotCount={3}
                                  showText={false}
                                />
                              </div>
                              <span className="text-slate-900 dark:text-white">
                                {job.description}
                              </span>
                              <span className="text-xs py-0.5 px-4 text-slate-400">
                                {getElapsedSeconds(job.started_at)}s
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ) : (timelineItems[selectedIndex]?.status === "Completed" ||
                    (typeof timelineItems[selectedIndex]?.status === "object" &&
                      timelineItems[selectedIndex]?.status?.current_status ===
                        "Completed")) &&
                  timelineItems[selectedIndex]?.recent_completed_jobs ? (
                  <div>
                    <div className="text-lg pb-4 px-4">
                      {podCurrentStatusToDisplayString(
                        timelineItems[selectedIndex]?.current_state
                      ) ||
                        podCurrentStatusToMachineString(
                          timelineItems[selectedIndex]?.current_state
                        )}
                    </div>
                    <div className="w-full max-w-xl font-mono text-sm flex justify-center items-center flex-col gap-2 bg-slate-100/90 dark:bg-slate-900/20 rounded-2xl p-6 border border-slate-200/60 dark:border-transparent">
                      <div className="flex flex-col gap-2">
                        {timelineItems[selectedIndex].recent_completed_jobs.map(
                          (job, index) => (
                            <div
                              key={job._id}
                              className="flex items-start text-slate-400"
                            >
                              <div className="w-8 flex-none relative left-1">
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="text-slate-400"
                                />
                              </div>
                              <span className="text-slate-900 dark:text-white">
                                {job.description}
                              </span>
                              <span className="text-xs py-0.5 px-4 text-slate-400">
                                {job.duration_seconds}s
                              </span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ) : timelineItems[selectedIndex]?.notes ? (
                  <div className="w-full max-w-xl font-mono text-sm flex justify-center items-center flex-col gap-2 bg-slate-100/90 dark:bg-slate-900/20 rounded-2xl p-6 border border-slate-200/60 dark:border-transparent">
                    {/* <div className="bg-white dark:bg-slate-700 rounded-xl p-6 shadow-sm">
                    <div
                      className="text-slate-700 dark:text-slate-300 leading-relaxed"
                      dangerouslySetInnerHTML={{
                        __html: timelineItems[selectedIndex].notes,
                      }}
                    />
                  </div> */}
                    <div className="flex flex-col gap-2 ">
                      <div className="flex items-center  text-slate-400">
                        <div className="w-8 flex-none relative left-1">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-slate-400"
                          />
                        </div>
                        <span>
                          Document received and initial review complete
                        </span>
                      </div>
                      <div className="flex items-center  text-slate-400">
                        <div className="w-8 flex-none relative left-1">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="text-slate-400"
                          />
                        </div>
                        <span>
                          Extracting pension plan details systematically
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-8 flex-none">
                          <LoadingIndicator
                            dotSize={3}
                            dotCount={3}
                            showText={false}
                          />
                        </div>
                        <span>
                          Validating data completeness for LoA generation
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full max-w-xl font-mono text-sm flex justify-center items-center flex-col gap-4rounded-2xl p-8">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">
                        Working on it...
                      </h3>
                      <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                        Nothing to do yet. We're processing this stage and will
                        let you know when we need your input.
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        <div className="absolute inset-0 z-0 overflow-hidden  ">
          <div className="ani-blob1 absolute w-64 h-64 bg-slate-500/30 rounded-full blur-3xl -left-16 -top-16"></div>
          <div className="ani-blob2 absolute w-96 h-1/2 bg-slate-500/20 rounded-full blur-3xl -right-20 top-1/2"></div>
        </div>
      </div>
    );
  };

  // Main render with tabs
  return (
    <>
      <div
        ref={containerRef}
        className="flex h-full min-h-72 flex-col"
        style={
          height
            ? { height: `${totalHeight}px`, maxHeight: `${totalHeight}px` }
            : undefined
        }
      >
        <Tabs
          tabs={[
            {
              id: "overview",
              label: "Overview",
              icon: <FontAwesomeIcon icon={faTable} />,
              content: renderOverviewTable(),
            },
            {
              id: "timeline",
              label: "Timeline",
              icon: <FontAwesomeIcon icon={faTimelineArrow} />,
              content: renderTimelineView(),
            },
          ]}
          activeTab={activeTab}
          onChange={setActiveTab}
          staticTabs
        />
      </div>

      {/* Drawer for displaying actions */}
      <Drawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedStageForDrawer(null);
        }}
        title={selectedStageForDrawer?.name || "Actions Required"}
        width="max-w-2xl"
      >
        <div className="px-6 pb-6 text-slate-900 dark:text-white">
          <div className="text-2xl mb-4 font-semibold text-slate-900 dark:text-slate-100">
            We've got some bits we need to sort...
          </div>

          {selectedStageForDrawer?.call_to_actions &&
          selectedStageForDrawer.call_to_actions.length > 0 ? (
            <div className="space-y-3 ">
              <div className="group flex items-start gap-4 p-4">
                <div className="flex-1 min-w-0 space-y-4">
                  <div className="grid grid-cols-1 gap-4 text-sm">
                    {selectedStageForDrawer.call_to_actions.map(
                      (action: any, index: number) => (
                        <div
                          key={action.id || index}
                          className="p-4 flex flex-col rounded-xl space-y-4 transition-all duration-200 bg-white dark:bg-slate-700 hover:shadow-sm"
                        >
                          <CallToAction action={action} />
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-500 dark:text-slate-400">
                No actions required at this time.
              </p>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
}
